import { apiFetch } from './client';

/**
 * Per-user permission schema (agents + limited users) — the backend's
 * authz/agentPermissionSubset.ts holds the canonical version. Keep this in
 * sync when new scopes/actions land.
 *
 * Enforcement semantics (backend requireModulePermission):
 *   - `read` is default-ALLOW: only an explicit `false` blocks the module.
 *   - `edit` / `create` / `publish` are default-DENY: only an explicit
 *     `true` grants the action (and only where the API's role gate lists
 *     the role — see the editor's per-scope descriptions).
 */
export const PERMISSION_SCHEMA = {
  ivr: ['read', 'edit', 'publish'],
  recordings: ['read', 'edit'],
  folders: ['read', 'edit'],
  voicemail: ['read', 'edit'],
  conference: ['read', 'edit'],
  callers: ['read', 'edit'],
  auth_lists: ['read', 'edit'],
  contacts: ['read', 'edit'],
  telephony: ['read', 'edit'],
  payments: ['read', 'edit'],
  billing: ['read', 'edit'],
  shop: ['read', 'edit'],
  courses: ['read', 'edit'],
  surveys: ['read', 'edit'],
  quizzes: ['read', 'edit'],
  points: ['read', 'edit'],
  forum: ['read', 'edit'],
  sms: ['read', 'edit'],
  reports: ['read'],
  customers: ['read', 'edit', 'create'],
  users: ['read', 'edit', 'create'],
} as const;

export type PermissionScope = keyof typeof PERMISSION_SCHEMA;
export type AgentPermissions = Record<string, Record<string, boolean>>;

export const agentPermissions = {
  get: (userId: string) =>
    apiFetch<{ permissions: AgentPermissions }>(`/api/agents/${userId}/permissions`),
  put: (userId: string, permissions: AgentPermissions) =>
    apiFetch<{ ok: true }>(`/api/agents/${userId}/permissions`, {
      method: 'PUT',
      body: JSON.stringify({ permissions }),
    }),
  /** Own matrix — non-governed roles receive `{}` (unrestricted). */
  me: () => apiFetch<{ permissions: AgentPermissions }>('/api/agents/me/permissions'),
};
